import { lazy, Suspense, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch, fetchMe } from '../lib/api';
import type { ApiError } from '../lib/api';

// Lazy-loaded: the delete-account danger zone (+ its confirm/sudo modals) is a
// rarely-reached settings surface, so keep it out of the initial JS bundle.
const DeleteAccountSection = lazy(() =>
  import('./DeleteAccountSection').then((m) => ({
    default: m.DeleteAccountSection,
  })),
);

export function AccountSection() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['me'],
    queryFn: fetchMe,
  });
  const [exporting, setExporting] = useState(false);
  const [exportDone, setExportDone] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  async function handleExport() {
    setExporting(true);
    setExportError(null);
    try {
      const res = await apiFetch('/api/export-data', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        // Full GDPR export. The `all` bucket (T-0095) combines
        // metadata+messages+tokens in one round-trip and needs no phrase
        // confirmation — the button click is the consent. (The old
        // `confirmation: true` was a bogus boolean the backend's
        // `Option<String>` field rejected → 422; dropped.)
        body: JSON.stringify({ bucket: 'all' }),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as Partial<ApiError>;
        throw new Error(err.message ?? `Export failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tizok-export-${new Date().toISOString().slice(0, 10)}.ndjson`;
      a.click();
      URL.revokeObjectURL(url);
      setExportDone(true);
    } catch (e) {
      setExportError(e instanceof Error ? e.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  }

  if (isLoading) {
    return (
      <div className="settings-section settings-section-loading" role="status">
        Loading account…
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="settings-section settings-section-error" role="alert">
        Could not load account details.
      </div>
    );
  }

  return (
    <div className="settings-section">
      <h3>Account</h3>
      <dl className="settings-dl">
        <dt>Display name</dt>
        <dd>{data.display_name}</dd>
        <dt>Email</dt>
        <dd>{data.email}</dd>
        <dt>User ID</dt>
        <dd className="settings-monospace">{data.user_id}</dd>
      </dl>
      <p className="settings-hint">
        Editing your display name, email, or password from here is coming soon.
        For now, use the CLI: <code>tizok account</code>.
      </p>
      <div className="settings-actions">
        <button
          type="button"
          className="settings-action-secondary"
          onClick={handleExport}
          disabled={exporting}
          aria-label="Export my data (GDPR)"
        >
          {exporting ? 'Exporting…' : 'Export my data'}
        </button>
      </div>
      {exportDone && (
        <p className="settings-success" role="status">
          Export downloaded. The file contains all your account data in NDJSON format.
        </p>
      )}
      {exportError && (
        <p className="settings-error" role="alert">
          {exportError}
        </p>
      )}
      <Suspense fallback={null}>
        <DeleteAccountSection />
      </Suspense>
    </div>
  );
}
